/**
 * OAuth Connection Hook
 *
 * React hook for managing OAuth authorization flows for connections.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/auth/useAuth';
import { isArmMockMode } from '@/services/armConfig';
import {
  buildOAuthRedirectUrl,
} from '@/services/oauthService';
import { OAUTH_CHANNEL_NAME } from '@/services/oauthChannel';
import {
  createConnection,
  syncConnection,
  deleteConnection,
  fetchConnectionRuntimeUrl,
  testConnection,
  listConsentLinks,
  confirmConsentCode,
  type CreateConnectionParams,
  type ConnectionResource,
  type TestConnectionResult,
  type AppResourceContext,
} from '@/services/connectionApi';
import { telemetryService } from '@/services/telemetryService';
import { isServiceProviderConnector } from '@/utils/connections';
import { useAppContextBuilder } from './useConnectionMutations';

// =============================================================================
// TYPES
// =============================================================================

/**
 * OAuth connection creation parameters
 */
export interface OAuthConnectionParams {
  /** Connection display name */
  connectionName: string;
  /** Connector ID (e.g., /subscriptions/.../managedApis/office365) */
  connectorId: string;
  /** Additional parameters for the connection */
  parameterValues?: Record<string, unknown>;
  /** Non-secret connection parameters to persist on the resource */
  connectionParameters?: Record<string, unknown>;
  /** Parameter set name (for multi-auth connectors) */
  parameterSetName?: string;
}

/**
 * OAuth re-authentication parameters (for existing connections)
 */
export interface OAuthReauthenticateParams {
  /** Name of the existing connection to re-authenticate */
  connectionName: string;
}

/**
 * OAuth connection flow state
 */
export type OAuthFlowState =
  | 'idle'
  | 'creating'
  | 'authenticating'
  | 'confirming'
  | 'syncing'
  | 'testing'
  | 'success'
  | 'error';

/**
 * Result from createOAuthConnection - includes both connection and any error
 * so callers don't have to rely on async state updates for error detection
 */
export interface OAuthConnectionResult {
  /** Created connection, or null if flow failed/cancelled */
  connection: ConnectionResource | null;
  /** Error message if flow failed (available synchronously, not via React state) */
  error?: string;
}

/**
 * Return type for useOAuthConnection hook
 */
export interface UseOAuthConnectionReturn {
  /** Current state of the OAuth flow */
  state: OAuthFlowState;
  /** Error message if flow failed */
  error: string | null;
  /** Created connection resource (if successful) */
  connection: ConnectionResource | null;
  /** Test result (if connection was tested) */
  testResult: TestConnectionResult | null;
  /** Initiate OAuth connection creation */
  createOAuthConnection: (params: OAuthConnectionParams) => Promise<OAuthConnectionResult>;
  /** Re-authenticate an existing OAuth connection */
  reauthenticateConnection: (params: OAuthReauthenticateParams) => Promise<boolean>;
  /** Cancel any ongoing OAuth flow */
  cancel: () => void;
  /** Reset state to idle */
  reset: () => void;
  /** Delete an abandoned, still-uncommitted successful connection, then reset to idle */
  discard: () => Promise<void>;
  /** Whether an OAuth flow is in progress */
  isInProgress: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for creating OAuth-based connections
 *
 * Flow:
 * 1. Create connection resource in Azure (without auth)
 * 2. Initiate OAuth popup flow
 * 3. User authorizes in popup
 * 4. Confirm consent code
 * 5. Test connection
 */

// Polling constants (matching useReconnectConnection)
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;
const POLLING_INTERVAL_MS = 500;
// COOP on the identity provider can sever the popup reference while the popup is still open.
// Keep callback listeners active briefly instead of treating popup.closed as immediate cancellation.
const POPUP_CLOSED_GRACE_MS = 3 * 60 * 1000;

export function useOAuthConnection(): UseOAuthConnectionReturn {
  const [state, setState] = useState<OAuthFlowState>('idle');
  const [error, setErrorState] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionResource | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  // Reflected in `isInProgress` while an abandoned-connection DELETE is settling.
  const [isDiscarding, setIsDiscarding] = useState(false);

  const { account, getOttoToken } = useAuth();
  const appContext = useAppContextBuilder();

  // Track if component is mounted for async operations
  const isMounted = useRef(true);
  const cancelledRef = useRef(false);
  // The connection a successful-but-uncommitted create flow owns, captured WITH the app
  // context that created it so cleanup deletes it in THAT app even if the selection later
  // changes. Only such a connection is safe for `discard` to delete. A failed rollback keeps
  // the marker so the next create/discard retries it.
  const discardableConnectionRef = useRef<{ connectionName: string; ctx: AppResourceContext } | null>(null);
  // True while a create/reauthenticate flow is running. Serializes the hook: a second flow is
  // rejected up front (so two flows can never overlap and PUT the same connection name), and
  // `reset` uses it to know an in-flight flow still owns the rollback of the resource it created.
  const flowActiveRef = useRef(false);
  // True while a discard DELETE is in flight, to dedupe concurrent discards of the same resource.
  const discardingRef = useRef(false);
  // Mirror error state in a ref so we can access it synchronously in async functions
  const errorRef = useRef<string | null>(null);

  // Helper to set error in both state and ref
  const setError = useCallback((err: string | null) => {
    errorRef.current = err;
    setErrorState(err);
  }, []);
  const pollIntervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);
  const pollStartTimeRef = useRef<number>(0);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const popupRef = useRef<Window | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const popupClosedTimerRef = useRef<number | null>(null);
  const popupFlowResolveRef = useRef<((code: string | null) => void) | null>(null);

  // Helper to clean up polling interval, message listener, and popup
  const cleanupOAuthResources = useCallback(() => {
    if (popupFlowResolveRef.current) {
      const resolvePopupFlow = popupFlowResolveRef.current;
      popupFlowResolveRef.current = null;
      resolvePopupFlow(null);
      return;
    }

    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupClosedTimerRef.current !== null) {
      clearTimeout(popupClosedTimerRef.current);
      popupClosedTimerRef.current = null;
    }
    isProcessingRef.current = false;
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (popupRef.current) {
      try { popupRef.current.close(); } catch { /* ignore */ }
      popupRef.current = null;
    }
    if (broadcastChannelRef.current) {
      try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
      broadcastChannelRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      cleanupOAuthResources();
    };
  }, [cleanupOAuthResources]);

  /**
   * Run the OAuth popup flow - shared logic for both create and reauthenticate
   * Opens popup, waits for OAuth callback via postMessage/BroadcastChannel/polling
   * @returns OAuth code (string), empty string (Azure handled auth after reaching callback), or null (error/cancelled/timeout)
   */
  const runOAuthPopupFlow = useCallback(
    async (consentLink: string): Promise<string | null> => {
      // Open OAuth popup
      const popup = window.open(
        consentLink,
        'oauth-popup',
        'width=600,height=700,scrollbars=yes'
      );

      if (!popup) {
        return null; // Caller should handle popup blocked error
      }

      // Clean up any previous polling/listener state before starting new ones
      cleanupOAuthResources();
      popupRef.current = popup;
      pollStartTimeRef.current = Date.now();

      return new Promise<string | null>((resolve) => {
        // Track if we successfully reached the OAuth callback page.
        // This distinguishes "user closed popup before completing auth" from
        // "user completed auth and popup closed after callback".
        let reachedCallbackPage = false;

        const cleanup = () => {
          window.removeEventListener('message', messageHandler);
          messageHandlerRef.current = null;
          if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (popupClosedTimerRef.current !== null) {
            clearTimeout(popupClosedTimerRef.current);
            popupClosedTimerRef.current = null;
          }
          if (broadcastChannelRef.current) {
            try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
            broadcastChannelRef.current = null;
          }
          isProcessingRef.current = false;
          try { popup.close(); } catch { /* ignore */ }
          popupRef.current = null;
          popupFlowResolveRef.current = null;
        };

        const resolveWithCleanup = (code: string | null) => {
          cleanup();
          if (code !== null && code !== '') {
            isProcessingRef.current = true;
          }
          resolve(code);
        };
        popupFlowResolveRef.current = resolveWithCleanup;

        // Listen for postMessage from OAuthCallbackPage (more reliable than URL polling)
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || typeof event.data !== 'object') return;
          const { type, code, error } = event.data;
          const isOAuthMessage = type === 'oauth-callback' || code !== undefined || error !== undefined;
          if (!isOAuthMessage) return;
          if (code !== undefined && typeof code !== 'string') return;
          if (error !== undefined && typeof error !== 'string') return;

          if (error) {
            resolveWithCleanup(null);
          } else {
            reachedCallbackPage = true;
            resolveWithCleanup(code ?? '');
          }
        };
        window.addEventListener('message', messageHandler);
        messageHandlerRef.current = messageHandler;

        // Also listen via BroadcastChannel (fallback for when COOP blocks window.opener)
        if ('BroadcastChannel' in window) {
          try {
            const channel = new BroadcastChannel(OAUTH_CHANNEL_NAME);
            channel.onmessage = (event: MessageEvent) => {
              const data = event.data;
              if (!data || typeof data !== 'object') return;
              const { type, code, error } = data;
              const isOAuthMessage = type === 'oauth-callback' || code !== undefined || error !== undefined;
              if (!isOAuthMessage) return;
              if (code !== undefined && typeof code !== 'string') return;
              if (error !== undefined && typeof error !== 'string') return;

              if (error) {
                resolveWithCleanup(null);
              } else {
                reachedCallbackPage = true;
                resolveWithCleanup(code ?? '');
              }
            };
            broadcastChannelRef.current = channel;
          } catch {
            // BroadcastChannel not supported
          }
        }

        pollIntervalRef.current = window.setInterval(() => {
          const elapsed = Date.now() - pollStartTimeRef.current;

          // Check for timeout
          if (elapsed > POLLING_TIMEOUT_MS) {
            resolveWithCleanup(null);
            return;
          }

          // Check if popup was closed by user
          try {
            if (popup.closed) {
              if (pollIntervalRef.current !== null) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }

              if (popupClosedTimerRef.current === null) {
                const remaining = Math.max(0, POLLING_TIMEOUT_MS - elapsed);
                const graceMs = Math.min(remaining, POPUP_CLOSED_GRACE_MS);
                popupClosedTimerRef.current = window.setTimeout(() => {
                  resolveWithCleanup(reachedCallbackPage ? '' : null);
                }, graceMs);
              }

              return;
            }
          } catch {
            // COOP may block access
          }

          // Skip if already processing
          if (isProcessingRef.current) return;

          try {
            const popupUrl = popup.location.href;
            if (popupUrl.includes(window.location.origin)) {
              const urlParams = new URLSearchParams(new URL(popupUrl).search);
              const code = urlParams.get('code');
              const error = urlParams.get('error');

              if (code) {
                resolveWithCleanup(code);
                return;
              }

              if (error) {
                resolveWithCleanup(null);
                return;
              }

              if (popupUrl.includes('/oauth/callback')) {
                reachedCallbackPage = true;
                resolveWithCleanup('');
                return;
              }
            }
          } catch {
            // Cross-origin error is expected while user is on OAuth page
          }
        }, POLLING_INTERVAL_MS);
      });
    },
    [cleanupOAuthResources]
  );

  /**
   * Core OAuth flow - shared between create and reauthenticate
   * Handles: get consent link → popup → confirm → sync → test
   * @returns true on success, false on failure
   */
  const runOAuthFlowCore = useCallback(
    async (
      ctx: Awaited<ReturnType<typeof appContext.buildContext>>,
      connectionName: string,
      telemetryContext: { type: 'create' | 'reauthenticate'; connectorId?: string },
    ): Promise<boolean> => {
      if (!ctx || !account) return false;
      // The flow is stale once cancelled or unmounted; checked after every await so it can't
      // resume and clobber a later flow's state. (Serialized re-entry guarantees one flow.)
      const isStale = () => !isMounted.current || cancelledRef.current;

      // Step 1: Initiate OAuth flow
      setState('authenticating');
      const redirectUrl = buildOAuthRedirectUrl();

      // Get consent link from Azure
      const { consentLink } = await listConsentLinks(ctx, {
        connectionName,
        redirectUrl,
        objectId: account?.localAccountId ?? account?.homeAccountId ?? 'local-user',
        tenantId: account?.tenantId ?? 'local-tenant',
      });

      // Bail before opening a popup if the flow was cancelled/unmounted during the consent fetch.
      if (isStale()) return false;

      // Step 2: Run OAuth popup flow (shared helper)
      const oauthCode = await runOAuthPopupFlow(consentLink);

      if (isStale()) return false;

      // null = user cancelled (closed popup), popup blocked, timeout, or explicit error
      if (oauthCode === null) {
        setError('Authorization was cancelled or failed. Please try again.');
        setState('error');
        return false;
      }

      // Step 3: Confirm consent code if we got one
      if (oauthCode) {
        setState('confirming');

        const freshToken = await getOttoToken();
        if (!freshToken && !isArmMockMode()) {
          setError('Failed to acquire token for consent confirmation');
          setState('error');
          return false;
        }

        // Bail before confirming consent if the flow went stale during token acquisition.
        // Matters most for reauthenticate, which has no created resource to roll back.
        if (isStale()) return false;

        await confirmConsentCode({ ...ctx, accessToken: freshToken as string }, {
          connectionName,
          code: oauthCode,
          objectId: account?.localAccountId ?? account?.homeAccountId ?? 'local-user',
          tenantId: account?.tenantId ?? 'local-tenant',
        });

        if (isStale()) return false;
      }

      // Step 4: Sync connection to finalize auth. Skip for service-provider connectors:
      // backend sync is a no-op for them (no app-MI ACL), and the redundant read can 404
      // on a just-created connection and trigger the caller's delete-on-failure cleanup.
      const connectorId = telemetryContext.connectorId;
      const isServiceProvider = !!connectorId && isServiceProviderConnector(connectorId);
      if (!isServiceProvider) {
        setState('syncing');
        await syncConnection(ctx, connectionName);

        if (isStale()) return false;
      }

      // Step 5: Test connection (with retry — Azure needs time to propagate consent).
      // Skip for service-provider connectors: the test is only a status read-back, which is
      // unreliable immediately after create (backend read-after-write can 404) and a false
      // failure would delete the just-created, successfully-consented connection. For these,
      // completing consent is the authentication signal.
      if (isServiceProvider) {
        setTestResult({ success: true, status: 'connected' });
      } else {
        setState('testing');
        let testResultData = await testConnection(ctx, connectionName);

        // Retry once after a delay if initial test fails (consent propagation delay)
        if (!testResultData.success && isMounted.current) {
          await new Promise((r) => setTimeout(r, 2000));
          if (isStale()) return false;
          testResultData = await testConnection(ctx, connectionName);
        }

        if (isStale()) return false;
        setTestResult(testResultData);

        if (!testResultData.success) {
          setError(testResultData.message ?? 'Connection test failed');
          setState('error');
          return false;
        }
      }

      // Success!
      setState('success');
      const eventName = telemetryContext.type === 'create'
        ? 'connection.oauth.completed'
        : 'connection.oauth.reauthenticate.completed';
      telemetryService.trackEvent(eventName, {
        connectionName,
        ...(telemetryContext.connectorId && { connectorName: telemetryContext.connectorId }),
      });

      return true;
    },
    [account, appContext, runOAuthPopupFlow, getOttoToken, setError]
  );

  /**
   * Delete the connection a create flow owns, against the app that created it (captured ctx).
   * Serialized via `discardingRef` so the flow's own rollback and an external `discard()` never
   * issue overlapping DELETEs for the same resource. Ownership is released only after a
   * successful DELETE, so a transient failure stays retryable. Identity-stable.
   */
  const deleteOwnedConnection = useCallback(async (): Promise<void> => {
    if (discardingRef.current) return;
    const owned = discardableConnectionRef.current;
    if (!owned) return;
    discardingRef.current = true;
    setIsDiscarding(true);
    try {
      await deleteConnection(owned.ctx, owned.connectionName);
      if (discardableConnectionRef.current === owned) {
        discardableConnectionRef.current = null;
      }
      telemetryService.trackEvent('connection.oauth.discarded.deleteConnection');
    } catch (deleteError) {
      // A 404 means the resource is already gone (e.g. a caller deleted it out-of-band, or a
      // prior DELETE succeeded server-side but its response was lost). Cleanup is idempotently
      // done, so release ownership; otherwise a stale marker would block every future create.
      // Other failures stay retryable — the marker is kept.
      if ((deleteError as { status?: number }).status === 404) {
        if (discardableConnectionRef.current === owned) {
          discardableConnectionRef.current = null;
        }
      } else {
        telemetryService.trackException(deleteError as Error, {
          context: 'connection.oauth.rollback.deleteConnection',
        });
      }
    } finally {
      discardingRef.current = false;
      setIsDiscarding(false);
    }
  }, []);

  /**
   * Create an OAuth connection
   */
  const createOAuthConnection = useCallback(
    async (params: OAuthConnectionParams): Promise<OAuthConnectionResult> => {
      // Serialize: reject re-entry synchronously (before any await) so a rapid second sign-in
      // can't start a parallel flow and PUT the same connection name.
      if (flowActiveRef.current) {
        return { connection: null, error: 'A sign-in is already in progress.' };
      }
      flowActiveRef.current = true;
      // Reset state
      cancelledRef.current = false;
      setError(null);
      setConnection(null);
      setTestResult(null);

      let createdConnection: ConnectionResource | null = null;

      try {
        // A prior flow may have failed to clean up its resource (its rollback DELETE failed),
        // leaving the only handle to that hidden credential here. Best-effort delete it before we
        // take ownership of a new resource, so it isn't permanently lost.
        if (discardableConnectionRef.current) {
          await deleteOwnedConnection();
          // If the cleanup DELETE failed the marker is still set. Proceeding would overwrite it
          // below on a successful create and permanently orphan the old hidden credential, so
          // abort instead and let the user retry.
          if (discardableConnectionRef.current) {
            const staleError = 'A previous sign-in could not be cleaned up. Please try again.';
            setError(staleError);
            setState('error');
            return { connection: null, error: staleError };
          }
        }

        // Build context
        const ctx = await appContext.buildContext();
        if (!ctx) {
          setState('error');
          return { connection: null, error: 'No app context available' };
        }

        // Get user info for OAuth
        if (!account) {
          const noUserError = 'No authenticated user found';
          setError(noUserError);
          setState('error');
          return { connection: null, error: noUserError };
        }

        // Step 1: Create connection resource (unique to create flow)
        setState('creating');
        telemetryService.trackEvent('connection.oauth.started', {
          connectorName: params.connectorId,
        });
        const createParams: CreateConnectionParams = {
          connectionName: params.connectionName,
          connectorId: params.connectorId,
          parameterValues: params.parameterValues ?? {},
          ...(params.parameterSetName && { parameterSetName: params.parameterSetName }),
          syncAfterCreation: false, // Defer sync until after OAuth flow completes
        };

        createdConnection = await createConnection(ctx, createParams);
        // We now own a server resource; capture it WITH this app's context so any teardown —
        // including the cancellation path just below — rolls it back against the creating app.
        discardableConnectionRef.current = { connectionName: params.connectionName, ctx };

        if (!isMounted.current || cancelledRef.current) {
          await deleteOwnedConnection();
          return { connection: null, error: 'Operation cancelled' };
        }

        setConnection(createdConnection);

        // Steps 2-6: Run shared OAuth flow (popup → confirm → sync → test)
        const success = await runOAuthFlowCore(ctx, params.connectionName, {
          type: 'create',
          connectorId: params.connectorId,
        });

        if (!success) {
          telemetryService.trackEvent('connection.oauth.failed.deleteConnection');
          await deleteOwnedConnection();
          // Clear connection state so UI doesn't show stale connection
          setConnection(null);
          // Return the error that was set by runOAuthFlowCore via errorRef
          return { connection: null, error: errorRef.current ?? 'Authorization was cancelled or failed. Please try again.' };
        }

        // Step 7: Fetch connectionRuntimeUrl (unique to create flow)
        if (!createdConnection.properties.connectionRuntimeUrl) {
          try {
            const runtimeUrl = await fetchConnectionRuntimeUrl(ctx, createdConnection.id);
            if (runtimeUrl) {
              createdConnection.properties.connectionRuntimeUrl = runtimeUrl;
            }
          } catch (runtimeUrlError) {
            console.warn('Failed to fetch runtime URL for authenticated connection', runtimeUrlError);
          }
        }

        // The discard marker set right after creation persists through success, so an
        // abandoned-but-uncommitted panel still rolls this resource back.
        return { connection: createdConnection };
      } catch (err) {
        // Roll back whatever we created before bailing, even when unmounted/cancelled.
        if (createdConnection) {
          telemetryService.trackEvent('connection.oauth.failed.deleteConnection');
          await deleteOwnedConnection();
        }

        if (!isMounted.current || cancelledRef.current) return { connection: null, error: 'Operation cancelled' };

        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setState('error');
        telemetryService.trackEvent('connection.oauth.failed', {
          connectorName: params.connectorId,
          error: errorMessage,
        });

        return { connection: null, error: errorMessage };
      } finally {
        flowActiveRef.current = false;
      }
    },
    [account, appContext, runOAuthFlowCore, setError, deleteOwnedConnection]
  );

  /**
   * Re-authenticate an existing OAuth connection
   * Used for refreshing expired tokens or updating consent
   */
  const reauthenticateConnection = useCallback(
    async (params: OAuthReauthenticateParams): Promise<boolean> => {
      // Serialize with create: reject re-entry so a reauth can't run alongside an in-flight flow.
      if (flowActiveRef.current) return false;
      flowActiveRef.current = true;
      // Reset state
      cancelledRef.current = false;
      setError(null);
      setConnection(null);
      setTestResult(null);

      try {
        // Build context
        const ctx = await appContext.buildContext();
        if (!ctx) {
          setError('No app context available');
          setState('error');
          return false;
        }

        // Get user info for OAuth
        if (!account) {
          setError('No authenticated user found');
          setState('error');
          return false;
        }

        telemetryService.trackEvent('connection.oauth.reauthenticate.started', {
          connectionName: params.connectionName,
        });

        // Run shared OAuth flow (popup → confirm → sync → test)
        const success = await runOAuthFlowCore(ctx, params.connectionName, {
          type: 'reauthenticate',
        });

        return success;
      } catch (err) {
        if (!isMounted.current || cancelledRef.current) return false;

        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setState('error');
        telemetryService.trackEvent('connection.oauth.reauthenticate.failed', {
          connectionName: params.connectionName,
          error: errorMessage,
        });

        return false;
      } finally {
        flowActiveRef.current = false;
      }
    },
    [account, appContext, runOAuthFlowCore, setError]
  );

  /**
   * Cancel any ongoing OAuth flow
   */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    cleanupOAuthResources();
    setState('idle');
    setError('Flow cancelled');
  }, [cleanupOAuthResources, setError]);

  /**
   * Reset state to idle
   */
  const reset = useCallback(() => {
    cancelledRef.current = true;
    cleanupOAuthResources();
    // A flow still in progress owns its own rollback (its cancel/failure path deletes the
    // resource it created), so clearing the marker here would strand it. Only release
    // ownership when there is no in-flight flow to hand it back to.
    if (!flowActiveRef.current) {
      discardableConnectionRef.current = null;
    }
    setState('idle');
    setError(null);
    setConnection(null);
    setTestResult(null);
  }, [cleanupOAuthResources, setError]);

  /**
   * Roll back an abandoned OAuth connection.
   *
   * Deletes the created resource only when the create flow reached success and the caller
   * never committed it, and always against the app that CREATED it (its captured context) so
   * an app/tenant switch can't delete a same-named connection elsewhere. Ownership is released
   * only after a successful DELETE, so a transient failure stays retryable. Identity-stable so
   * an unmount cleanup bound to it never re-fires mid-session.
   */
  const discard = useCallback(async (): Promise<void> => {
    // Reset the local flow immediately so the form returns to idle, but keep ownership of the
    // created resource until its DELETE actually succeeds (handled by deleteOwnedConnection).
    cancelledRef.current = true;
    cleanupOAuthResources();
    setState('idle');
    setError(null);
    setConnection(null);
    setTestResult(null);
    await deleteOwnedConnection();
  }, [cleanupOAuthResources, setError, deleteOwnedConnection]);

  return {
    state,
    error,
    connection,
    testResult,
    createOAuthConnection,
    reauthenticateConnection,
    cancel,
    reset,
    discard,
    isInProgress: (state !== 'idle' && state !== 'success' && state !== 'error') || isDiscarding,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting the OAuth redirect URL
 */
export function useOAuthRedirectUrl(): string {
  return buildOAuthRedirectUrl();
}

/**
 * Get descriptive text for OAuth flow state
 */
export function getOAuthStateMessage(state: OAuthFlowState): string {
  switch (state) {
    case 'idle':
      return 'Ready to connect';
    case 'creating':
      return 'Creating connection...';
    case 'authenticating':
      return 'Waiting for authorization...';
    case 'confirming':
      return 'Confirming authorization...';
    case 'syncing':
      return 'Syncing connection...';
    case 'testing':
      return 'Testing connection...';
    case 'success':
      return 'Connection created successfully!';
    case 'error':
      return 'Connection failed';
    default:
      return '';
  }
}